<template>
    <div id="graph-div" style="width: 100%; height: 500px; position: relative;">
        <vue-flow v-model="elements" :default-zoom="1" :min-zoom="0.2" :max-zoom="4" :node-dimensions="nodeDimensions"
            :fit-view-on-init="true" @pane-ready="onReady" @nodeMouseEnter="showTooltip" @nodeMouseLeave="hideTooltip">
            <template #node-custom="nodeProps">
                <div class="custom-node" :style="{ borderColor: nodeProps.data.level }">
                    <div>{{ nodeProps.data.name }}</div>
                </div>
            </template>
            <background pattern-color="#aaa" gap="16" />
            <controls />
            <mini-map />
        </vue-flow>
    </div>
</template>

<script>
import { VueFlow, Background, Controls, MiniMap, useVueFlow } from '@vue-flow/core';
import { computed, onMounted, ref } from 'vue';
import { DateTimeFormat } from '../../utils/commonFunction';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';

export default {
    name: 'PipelineFlow',
    components: {
        VueFlow,
        Background,
        Controls,
        MiniMap
    },
    props: ["pipelineDetails", "pipelineName"],
    setup(props) {
        const { fitView } = useVueFlow();
        const elements = ref([]);
        const nodeDimensions = ref({ width: 180, height: 40 });

        const onReady = () => {
            setTimeout(() => {
                fitView({ padding: 0.2 });
            }, 20);
        };

        const calculateNodes = computed(() => {
            // Create status to color mapping object to avoid multiple if-else checks
            const statusColorMap = {
                'SUCCEEDED': 'green',
                'SUBMITTED': 'yellow',
                'IN_PROGRESS': 'blue',
                'FAILED': 'red',
                'CANCELLED': 'grey'
            };

            let parentId = null;

            const nodeArray = [];
            const edgeArray = [];
            const rowHeight = 100; // Vertical space between rows
            const indent = 150; // Horizontal indent per depth level

            let index = 0;

            props.pipelineDetails.forEach((job) => {
                const jobDetail = job.jobDetails;

                if (!jobDetail || jobDetail.kind === 'PIPELINE' || jobDetail.name.indexOf("pipeline") !== -1) {
                    return;
                }

                const nodeId = `node-${jobDetail.name}-${index}`;

                // Create node
                nodeArray.push({
                    id: nodeId,
                    type: 'custom',
                    position: { x: index * indent, y: index * rowHeight },
                    data: {
                        name: jobDetail.name,
                        level: statusColorMap[jobDetail.last_run_status] || 'grey',
                        created_at: jobDetail.created_at,
                        last_run_status: jobDetail.last_run_status,
                        kind: jobDetail.kind
                    }
                });

                // Create edge connecting to parent if exists
                if (parentId) {
                    edgeArray.push({
                        id: `edge-${parentId}-${nodeId}`,
                        source: parentId,
                        target: nodeId,
                        type: 'smoothstep',
                        animated: true,
                        style: { stroke: 'black' }
                    });
                }

                parentId = nodeId;
                index++;
            });

            return [...nodeArray, ...edgeArray];
        });

        onMounted(() => {
            elements.value = calculateNodes.value;
        });

        return {
            elements,
            nodeDimensions,
            onReady
        };
    },
    methods: {
        showTooltip(event) {
            // Extract node details and mouse event
            const { id } = event.node;
            const mouseEvent = event.event;

            // Find the node data
            const node = this.elements.find(el => el.id === id && !el.source);

            if (!node) return;

            // Create tooltip element
            const tooltip = document.createElement('div');
            tooltip.innerHTML = `
          <div style='font-size: 12px;'>
            <strong>Name: </strong> ${node.data.name} <br>
            <strong>Kind: </strong> ${node.data.kind} <br>
            <strong>Created At: </strong> ${DateTimeFormat(node.data.created_at)}
          </div>
        `;

            // Position the tooltip near the mouse
            tooltip.style.left = mouseEvent.pageX + 5 + 'px';
            tooltip.style.top = mouseEvent.pageY + 5 + 'px';

            // Remove any existing tooltips
            const existingTooltip = document.querySelector('.vueflow-tooltip');
            if (existingTooltip) {
                existingTooltip.remove();
            }

            // Add tooltip class for easy removal
            tooltip.classList.add('vueflow-tooltip');

            // Add to document
            document.body.appendChild(tooltip);
        },

        hideTooltip() {
            // Remove tooltip when mouse leaves node
            const existingTooltip = document.querySelector('.vueflow-tooltip');
            if (existingTooltip) {
                existingTooltip.remove();
            }
        }
    }
};
</script>

<style>
.vue-flow {
    width: 100%;
    height: 100%;
}

.vue-flow__node {
    cursor: pointer;
}

.custom-node {
    padding: 10px 15px;
    border-radius: 5px;
    border: 2px solid;
    background: white;
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 150px;
}

.vue-flow__edge path {
    stroke: #ccc;
    stroke-width: 2px;
}

.vueflow-tooltip {
    position: absolute;
    pointer-events: none;
    border-radius: 4px;
    z-index: 1000;
    background-color: #6E84A3;
    color: #fff;
    visibility: visible !important;
}
</style>